import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDashboardPermission } from '@euler/model/bi/dashboard';
import {
  CirclePlus,
  FileDown,
  LayoutList,
  LayoutTemplate,
  Search,
} from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, TextInput, View } from 'react-native';
import { BiNamePanel } from '../../components/BiNamePanel';
import { BiMenuRow } from '../../components/ui/BiMenuRow';
import { BiMenuSheet } from '../../components/ui/BiMenuSheet';
import { BiRetry } from '../../components/ui/BiRetry';
import { biErrorMessage } from '../../functions/biErrors';
import { biIsStaffDashboard } from '../../functions/biIsStaffDashboard';
import { useBiSetDefault } from '../functions/useBiSetDefault';
import { PickerRow } from './PickerRow';
import { TemplateSheet } from './TemplateSheet';
import { useBiDashboardPicker } from './functions/useBiDashboardPicker';

/** From this many dashboards the picker offers a name filter. */
const kSearchFrom = 8;

/**
 * The header title's picker: switch dashboards — one's own (the reports,
 * my dashboard), then staff dashboards tagged by role, then those shared,
 * filtered by name when there are many — make one the default, create
 * one blank or from a template, manage them all on the list page, or open
 * the export history.
 */
export const DashboardPicker = memo(
  ({
    visible,
    currentId,
    onClose,
    onSwitch,
    onManage,
    onOpenExports,
    onDefaultChanged,
  }: {
    visible: boolean;
    currentId: string;
    onClose: () => void;
    /** Opens another dashboard in place of this one. */
    onSwitch: (dashboardId: string) => void;
    onManage: () => void;
    onOpenExports: () => void;
    onDefaultChanged: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, refresh, create } = useBiDashboardPicker(visible);
    const { setDefault, settingId } = useBiSetDefault();
    const [defaultError, setDefaultError] = useState<string | null>(null);
    const [naming, setNaming] = useState(false);
    const [templating, setTemplating] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // A manager may hold a staff dashboard per colleague: find one by name.
    const [query, setQuery] = useState('');
    useEffect(() => {
      if (visible) setQuery('');
    }, [visible]);
    const all = state.kind === 'ready' ? state.items : [];
    const searchable = all.length > kSearchFrom;
    const text = query.trim();
    const needle = text.toLowerCase();
    const shown = text
      ? all.filter(
          item =>
            item.name.toLowerCase().includes(needle) ||
            !!item.subjectName?.toLowerCase().includes(needle),
        )
      : all;

    const submitName = async (name: string) => {
      setBusy(true);
      setError(null);
      try {
        const dashboard = await create(name);
        setNaming(false);
        onSwitch(dashboard.id);
      } catch (e) {
        console.error('BI dashboard create failed', e);
        setError(biErrorMessage(e, '没有创建成功，请稍后重试。'));
      } finally {
        setBusy(false);
      }
    };

    return (
      <>
        <BiMenuSheet visible={visible} title="切换看板" onClose={onClose}>
          {run => (
            <>
              {defaultError ? (
                <Typography.Text size={rpx(12)} color={aiColors.destructive}>
                  {defaultError}
                </Typography.Text>
              ) : null}
              {state.kind === 'ready' && searchable ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: rpx(8),
                    height: rpx(36),
                    paddingHorizontal: rpx(10),
                    borderRadius: rpx(10),
                    backgroundColor: aiColors.input,
                  }}
                >
                  <Search size={14} color={aiColors.mutedForeground} />
                  <TextInput
                    accessibilityLabel="按名称筛选看板"
                    value={query}
                    onChangeText={setQuery}
                    placeholder="按看板或员工姓名筛选"
                    placeholderTextColor={aiColors.mutedForeground}
                    style={{
                      flex: 1,
                      color: aiColors.foreground,
                      fontSize: rpx(13),
                    }}
                  />
                </View>
              ) : null}
              {state.kind === 'ready' && text && !shown.length ? (
                <Typography.Text
                  size={rpx(13)}
                  color={aiColors.mutedForeground}
                  style={{ paddingVertical: rpx(12), textAlign: 'center' }}
                >
                  没有名称包含「{text}」的看板
                </Typography.Text>
              ) : null}
              {state.kind === 'ready' ? (
                <ScrollView
                  style={{ maxHeight: rpx(360) }}
                  keyboardShouldPersistTaps="handled"
                >
                  {groupsOf(shown).map(([label, items]) => [
                    label ? (
                      <Typography.Text
                        key={label}
                        size={rpx(11)}
                        color={aiColors.mutedForeground}
                        style={{
                          paddingTop: rpx(10),
                          paddingHorizontal: rpx(4),
                        }}
                      >
                        {label}
                      </Typography.Text>
                    ) : null,
                    ...items.map(item => (
                      <PickerRow
                        key={item.id}
                        item={item}
                        current={item.id === currentId}
                        onOpen={
                          item.id === currentId
                            ? onClose
                            : run(() => onSwitch(item.id))
                        }
                        setting={settingId === item.id}
                        onSetDefault={async () => {
                          setDefaultError(null);
                          const outcome = await setDefault(item.id);
                          if (!outcome) return;
                          if (!outcome.ok) {
                            setDefaultError(outcome.message);
                            return;
                          }
                          refresh();
                          onDefaultChanged();
                        }}
                      />
                    )),
                  ])}
                </ScrollView>
              ) : (
                <View
                  style={{ paddingVertical: rpx(16), alignItems: 'center' }}
                >
                  {state.kind === 'failed' ? (
                    <BiRetry onRetry={refresh} />
                  ) : (
                    <ActivityIndicator color={aiColors.primary} />
                  )}
                </View>
              )}
              <View
                style={{
                  height: 1,
                  backgroundColor: aiColors.border,
                  marginVertical: rpx(4),
                }}
              />
              <BiMenuRow
                icon={CirclePlus}
                label="新建看板"
                onPress={run(() => {
                  setError(null);
                  setNaming(true);
                })}
              />
              <BiMenuRow
                icon={LayoutTemplate}
                label="从模板新建"
                onPress={run(() => setTemplating(true))}
              />
              <BiMenuRow
                icon={LayoutList}
                label="管理全部看板"
                onPress={run(onManage)}
              />
              <BiMenuRow
                icon={FileDown}
                label="导出记录"
                onPress={run(onOpenExports)}
              />
            </>
          )}
        </BiMenuSheet>
        <TemplateSheet
          visible={templating}
          onClose={() => setTemplating(false)}
          onCreated={onSwitch}
        />
        <BiNamePanel
          visible={naming}
          title="新建看板"
          initialName=""
          confirmTitle="创建"
          error={error}
          busy={busy}
          onConfirm={submitName}
          onClose={() => setNaming(false)}
        />
      </>
    );
  },
);

type PickerItem = Parameters<typeof biIsStaffDashboard>[0] & {
  permission: BiDashboardPermission;
};

/**
 * The picker's groups in its order (each keeps the list's order): one's
 * own, staff dashboards, shared ones. The first goes unlabelled when it is
 * the only one.
 */
function groupsOf<T extends PickerItem>(items: T[]): [string | null, T[]][] {
  const own = items.filter(
    item =>
      item.permission === BiDashboardPermission.Manage &&
      !biIsStaffDashboard(item),
  );
  const staff = items.filter(
    item =>
      item.permission === BiDashboardPermission.Manage &&
      biIsStaffDashboard(item),
  );
  const shared = items.filter(
    item => item.permission !== BiDashboardPermission.Manage,
  );
  const groups: [string | null, T[]][] = [
    ['我的看板', own],
    ['员工看板', staff],
    ['共享给我', shared],
  ].filter((group): group is [string, T[]] => group[1].length > 0) as [
    string,
    T[],
  ][];
  return groups.length === 1 ? [[null, groups[0][1]]] : groups;
}
